/**
 * MFA enrollment gate (2026-08-11).
 *
 * When a company mandates two-factor (companies.require_2fa), the
 * backend issues non-enrolled members a RESTRICTED session — the JWT
 * carries an `mfaEnroll: true` claim, and requireAuth confines it to
 * the 2FA-enrollment endpoints, /me, and /logout. Everything else 403s.
 *
 * Before this gate, the frontend ignored that signal entirely: a
 * restricted teammate logged in to a dashboard of empty panels and
 * errors with no explanation ("the app is down"). This screen catches
 * the claim at the top of the authed render and replaces the broken-
 * looking shell with the one thing the session CAN do: enrol.
 *
 * Completion flow: the backend does NOT reissue a token on
 * setup-verify, so the session stays restricted after enrolling. The
 * gate's final step is deliberate — "sign in again" — which both
 * refreshes the session AND walks the user through a real TOTP
 * challenge immediately, proving their authenticator works while the
 * setup is fresh.
 */

import React, { useState } from 'react';
import TwoFactorSetup from './TwoFactorSetup';

/**
 * Tolerant JWT payload decode — NOT verification (the server verifies;
 * this is presentation-only routing). Returns {} on anything weird so
 * a malformed token can never crash the shell.
 */
export function decodeTokenPayload(token) {
  try {
    const mid = String(token).split('.')[1];
    if (!mid) return {};
    const b64 = mid.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64)) || {};
  } catch {
    return {};
  }
}

/** Does this session require the enrollment gate? */
export function isMfaRestricted(token) {
  return decodeTokenPayload(token).mfaEnroll === true;
}

export default function MfaEnrollmentGate({ authToken, onSignOut }) {
  const [enrolled, setEnrolled] = useState(false);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24,
      background: 'var(--bg-primary, #07070F)',
    }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.18em',
          color: '#8B5CF6', textTransform: 'uppercase', marginBottom: 10,
        }}>
          TrailerCast · Security
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 8px', color: 'var(--text-primary, #fff)' }}>
          Set up two-factor authentication
        </h1>
        <p style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--text-secondary, #C4C4D4)', margin: '0 0 20px' }}>
          Your team requires two-factor authentication on every account.
          Grab an authenticator app (Google Authenticator, Authy,
          1Password…) and set it up below — it takes about two minutes,
          and you&apos;ll have full access right after.
        </p>

        <div style={{
          borderRadius: 14, padding: 20,
          background: 'var(--bg-secondary, #13131F)',
          border: '1px solid var(--border-color, #2D2D45)',
        }}>
          <TwoFactorSetup authToken={authToken} onEnabled={() => setEnrolled(true)} />
        </div>

        {enrolled && (
          <div style={{
            marginTop: 16, padding: 16, borderRadius: 12,
            background: 'rgba(134, 239, 172, 0.08)',
            border: '1px solid rgba(134, 239, 172, 0.35)',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#86EFAC', marginBottom: 4 }}>
              Two-factor is on — one last step
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary, #C4C4D4)', marginBottom: 12 }}>
              Save your backup codes above, then sign in again with your
              new 6-digit code to unlock the app.
            </div>
            <button
              type="button"
              onClick={onSignOut}
              style={{
                padding: '10px 18px', borderRadius: 8, cursor: 'pointer',
                fontWeight: 700, fontSize: 14, border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff',
              }}
            >
              Sign in again →
            </button>
          </div>
        )}

        {!enrolled && (
          <button
            type="button"
            onClick={onSignOut}
            style={{
              marginTop: 14, background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, color: 'var(--text-secondary, #7C7C9A)', textDecoration: 'underline', padding: 0,
            }}
          >
            Sign out
          </button>
        )}
      </div>
    </div>
  );
}
